import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewMemberTicker.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NewMemberTicker — horizontal scrolling marquee of recent signups.
 * - Polls /api/community/new-members every 30s for fresh entries
 * - Auto-detects @usernames in messages and turns them into clickable profile links
 * - 7-day rolling window (handled server-side)
 */
const NewMemberTicker = () => {
  const [items, setItems] = useState([]);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  const fetchFeed = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/community/new-members`);
      if (!res.ok) return;
      const data = await res.json();
      if (mountedRef.current) setItems(data.items || []);
    } catch (e) {
      // silent — ticker just won't update
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchFeed();
    const id = setInterval(fetchFeed, 30000);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [fetchFeed]);

  // Render a message with @username made clickable
  const renderMessage = (item) => {
    const msg = item.message || '';
    const username = item.username;
    const userId = item.user_id;
    if (!username || !userId) return <span>{msg}</span>;

    const handle = `@${username}`;
    const idx = msg.indexOf(handle);
    if (idx === -1) return <span>{msg}</span>;

    const before = msg.slice(0, idx);
    const after = msg.slice(idx + handle.length);

    return (
      <span>
        {before}
        <a
          href={`/profile/${userId}`}
          className="nmt-username"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/profile/${userId}`);
          }}
          data-testid={`new-member-link-${username}`}
        >
          {handle}
        </a>
        {after}
      </span>
    );
  };

  if (!items.length) return null;

  // Duplicate the list so the marquee loops seamlessly
  const loop = [...items, ...items];

  return (
    <div
      className="new-member-ticker"
      data-testid="new-member-ticker"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="nmt-label" data-testid="new-member-ticker-label">
        <span className="nmt-label-dot" />
        NEW MEMBERS
      </div>
      <div className="nmt-track-wrap">
        <div
          className={`nmt-track ${paused ? 'nmt-paused' : ''}`}
          style={{ animationDuration: `${Math.max(12, loop.length * 4)}s` }}
        >
          {loop.map((item, idx) => (
            <span
              key={`${item.id}-${idx}`}
              className={`nmt-item ${item.is_og40 || item.priority === 'important' ? 'nmt-og40' : ''}`}
            >
              {renderMessage(item)}
              <span className="nmt-divider">•</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewMemberTicker;
